import { useCallback, useRef, type MutableRefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeCardNumberElement,
} from '@stripe/stripe-js';
import {
  buildGoogleAdsUserData,
  GOOGLE_ADS_BILLING_DETAILS_KEY,
  sendPurchaseEvent,
  trackPaymentGateway,
  type GoogleAdsStripeBillingDetails,
} from '../../analytics';
import { EVENTS as E, PAYMENT_GATEWAY_PROPERTY } from '../constants';
import { selectCardholderName, selectID, stripePurchase } from '../store/checkout';
import { selectAnalyticsData, selectEventsData, setEventData } from '../store/events';
import { selectLanding, selectPlan, selectPlanCoupon, selectPlanDetails } from '../store/plans';
import {
  selectEmail,
  selectGoogleAdsGeo,
  selectPaymentProvider,
  selectToken,
  selectUserLocation,
  selectUuid,
} from '../store/signup';
import { resolveDynamicPLTVPriceForFlow, resolvePLTVPriceForFlow } from '../utils';
import { useSendEvents } from './useSendEvents';
import { useStripeAccount } from './useStripeAccount';

export interface PlanEventDetails {
  value: number;
  currency: string;
  price: number;
  duration: string;
  description: string;
  periodPrice: number;
  product_id: string;
}

export type StripePurchaseClick = (
  paymentMethod: PaymentRequestPaymentMethodEvent | null,
  successPayment?: () => void,
  failedPayment?: () => void,
) => void;

interface UseStripePurchaseParams {
  screenId: string;
  stripeInstance: Stripe | null;
  card: MutableRefObject<StripeCardNumberElement | null>;
  cardholder: MutableRefObject<HTMLInputElement | null>;
  getPlanDetails: () => PlanEventDetails;
  isTimerExpired: boolean;
}

export const useStripePurchase = ({
  screenId,
  stripeInstance,
  card,
  cardholder,
  getPlanDetails,
  isTimerExpired,
}: UseStripePurchaseParams) => {
  const dispatch = useDispatch();
  const sendEvents = useSendEvents({ screenId });
  const { stripeAccountName, stripeGateway } = useStripeAccount();

  const plan = useSelector(selectPlan);
  const planDetails = useSelector(selectPlanDetails);
  const couponDetails = useSelector(selectPlanCoupon);
  const paymentProvider = useSelector(selectPaymentProvider);
  const token = useSelector(selectToken);
  const eventsData = useSelector(selectEventsData);
  const analyticsParams = useSelector(selectAnalyticsData);
  const landing = useSelector(selectLanding);
  const userLocation = useSelector(selectUserLocation);
  const userGeo = useSelector(selectGoogleAdsGeo);
  const signupEmail = useSelector(selectEmail);
  const profileName = useSelector(selectCardholderName);
  const subscriptionId = useSelector(selectID);
  const userUuid = useSelector(selectUuid);

  const lastBillingDetailsRef = useRef<GoogleAdsStripeBillingDetails | null>(null);

  const cacheBillingDetailsForGoogleAds = useCallback(
    (details: GoogleAdsStripeBillingDetails | null) => {
      lastBillingDetailsRef.current = details;
      if (details) {
        dispatch(setEventData({ [GOOGLE_ADS_BILLING_DETAILS_KEY]: details }));
      }
    },
    [dispatch],
  );

  const sendPurchase = useCallback(
    (
      sub_id = subscriptionId,
      event_id = `${userUuid}_purchase`,
      paymentMethod = 'card',
      pltv?: number | null,
    ) => {
      const planData = getPlanDetails();
      const { name } = planDetails || {};
      const pLTVPrice = resolvePLTVPriceForFlow({
        landing,
        plan: name,
        paymentMethod,
        userLocation,
        useTimerDefault: isTimerExpired,
      });
      const dynamicPLTVPrice = resolveDynamicPLTVPriceForFlow({ landing, plan: name, pltv });

      dispatch(setEventData(planData));
      sendEvents(E.PLAN_CHOSEN, { ...eventsData, ...planData });
      sendPurchaseEvent({
        price: planData.periodPrice,
        currency: planData.currency,
        plan: name,
        subscriptionId: sub_id,
        event_id,
        eventsData: { ...eventsData, ...planData },
        isUpsell: false,
        isTrial: name.includes('intro'),
        ...(pLTVPrice !== undefined && { pLTVPrice }),
        ...(dynamicPLTVPrice !== undefined && { dynamicPLTVPrice }),
        userLocation,
        userData: buildGoogleAdsUserData({
          email: signupEmail,
          profileName,
          stripeBillingDetails: lastBillingDetailsRef.current,
          geo: userGeo,
        }),
      });
    },
    [
      dispatch,
      sendEvents,
      getPlanDetails,
      planDetails,
      eventsData,
      subscriptionId,
      userUuid,
      landing,
      userLocation,
      isTimerExpired,
      signupEmail,
      profileName,
      userGeo,
    ],
  );

  const stripePurchaseClick = useCallback<StripePurchaseClick>(
    (
      paymentMethod: PaymentRequestPaymentMethodEvent | null,
      successPayment?: () => void,
      failedPayment?: () => void,
    ) => {
      if (!stripeInstance || !card.current) {
        failedPayment?.();
        return;
      }

      cacheBillingDetailsForGoogleAds(
        paymentMethod?.paymentMethod.billing_details ??
          (cardholder.current?.value ? { name: cardholder.current.value } : null),
      );

      const paymentMethodName = paymentMethod?.walletName?.toLocaleLowerCase() ?? 'card';
      const paymentMethodObj = paymentMethod
        ? { methodName: paymentMethodName, stripeNonce: paymentMethod.paymentMethod.id }
        : null;
      const localEventsData = {
        payment_method: paymentMethodName,
        card_brand: eventsData.card_brand ?? 'unknown',
        stripe_account_name: stripeAccountName,
        payment_processor: 'Stripe',
        [PAYMENT_GATEWAY_PROPERTY]: stripeGateway,
      };
      const extendedEventsData = {
        ...eventsData,
        ...getPlanDetails(),
        ...localEventsData,
        screen_name: screenId,
      };
      const onSuccess = (
        sub_id: string,
        event_id: string,
        paymentData?: { pltv?: number | null },
      ) => {
        successPayment?.();
        sendPurchase(sub_id, event_id, paymentMethodName, paymentData?.pltv);
      };

      window?.ttq?.track('SubmitForm', {});
      dispatch(setEventData(localEventsData));
      stripePurchase({
        paymentMethodObj,
        stripe: stripeInstance,
        card: card.current,
        name: cardholder.current?.value,
        plan,
        token,
        analyticsParams,
        eventsData: extendedEventsData,
        successPayment: onSuccess,
        failedPayment,
        coupon: couponDetails?.couponName,
      })(dispatch);
    },
    [
      dispatch,
      eventsData,
      getPlanDetails,
      plan,
      token,
      analyticsParams,
      stripeInstance,
      card,
      cardholder,
      stripeAccountName,
      stripeGateway,
      couponDetails,
      screenId,
      sendPurchase,
      cacheBillingDetailsForGoogleAds,
    ],
  );

  const onPurchaseClick = () => {
    const paymentData = {
      payment_method: 'card',
      payment_processor: paymentProvider || 'Stripe',
      card_brand: eventsData.card_brand ?? 'unknown',
      stripe_account_name: stripeAccountName,
      [PAYMENT_GATEWAY_PROPERTY]: trackPaymentGateway(dispatch, stripeGateway),
      ...getPlanDetails(),
    };
    sendEvents(E.BUY_SUBSCRIPTION_TAPPED, paymentData);
    sendEvents(E.PROCESSING_STARTED, paymentData);
    stripePurchaseClick(null);
  };

  return { stripePurchaseClick, onPurchaseClick };
};
